import { useCallback, useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose, MdModeEdit } from "react-icons/md";
import { aging, qc } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Pagination from "../../common/Pagination";
import Button from "../../common/Button";
import { IoIosSave } from "react-icons/io";
import { AiOutlineCheck } from "react-icons/ai";
import Table from "../../common/Table";
import { LayoutGrid } from "lucide-react";
import { RiHistoryLine } from "react-icons/ri";
import Loader from "../../common/Loader";
import { formatDate } from "../../utlis/formateDate";
import InputField from "../../common/InputField";
import { Card, RegistryHeader, CalculationFooter } from "../../common/Shared";

const QC = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [isQcData, setIsQcData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [editingRow, setEditingRow] = useState(null);
  const [backupRow, setBackupRow] = useState(null);
  const [activeTab, setActiveTab] = useState("PENDING");
  const [isQcDoneData, setIsQcDoneData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef(null);

  const toInt = (v) =>
    v === "" || v === null || v === undefined ? 0 : parseInt(v, 10) || 0;
  const toFloat = (v) =>
    v === "" || v === null || v === undefined ? 0 : parseFloat(v) || 0;
  const getQcId = (r) => toInt(r.qC_Id ?? r.QC_Id ?? 0);

  const isRowEditable = (index) => editingRow === index;

  const normalizeRow = (r) => ({
    ...r,
    qc_Pcs: r.qc_Pcs ?? "",
    rejected_Pcs: r.rejected_Pcs ?? "",
    approved_Pcs: r.approved_Pcs ?? "",
    first_Piece: r.first_Piece ?? "",
    first_Weight: r.first_Weight ?? "",
    second_Piece: r.second_Piece ?? "",
    second_Weight: r.second_Weight ?? "",
    remark: r.remark ?? r.remarks ?? "",
  });

  const handleEdit = (rowIndex) => {
    setBackupRow(JSON.parse(JSON.stringify(isQcData[rowIndex])));
    setEditingRow(rowIndex);
  };

  const handleCancelEdit = (rowIndex) => {
    if (backupRow) {
      const updatedData = [...isQcData];
      updatedData[rowIndex] = backupRow;
      setIsQcData(updatedData);
    }
    setEditingRow(null);
    setBackupRow(null);
  };

  const fetchData = useCallback(async (page, tab) => {
    if (loading) return;
    setLoading(true);
    try {
      let res;
      if (tab === "PENDING") {
        res = await qc.getAllQC(page, pageSize);
        const rows = (res.data || []).map(normalizeRow);
        setIsQcData((prev) => (page === 1 ? rows : [...prev, ...rows]));
        setHasMore(rows.length === pageSize);
      } else {
        res = await aging.getAllQcDone(page, pageSize);
        const rows = res.data || [];
        setIsQcDoneData((prev) => (page === 1 ? rows : [...prev, ...rows]));
        setHasMore(rows.length === pageSize);
      }
    } catch (error) {
      notify("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchData(1, activeTab);
  }, [activeTab, fetchData]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 10 && hasMore && !loading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchData(nextPage, activeTab);
    }
  };

  const [saving, setSaving] = useState(false);
  const handleSubmit = async (rowIndex) => {
    if (saving) return;
    setSaving(true);
    try {
      const row = isQcData[rowIndex];
      const payload = {
        qC_Id: getQcId(row),
        batch_Id: row.batch_Id ?? "",
        casting_No: row.casting_No ?? "",
        order_No: row.order_No,
        die_Id: row.die_Id ?? "",
        produced_Pcs: toInt(row.produced_Pcs),
        qc_Pcs: toInt(row.qc_Pcs),
        rejected_Pcs: toInt(row.rejected_Pcs),
        reject_Reason: row.reject_Reason,
        approved_Pcs: toInt(row.approved_Pcs),
        first_Piece: toInt(row.first_Piece),
        first_Weight: toFloat(row.first_Weight),
        second_Piece: toInt(row.second_Piece),
        second_Weight: toFloat(row.second_Weight),
        remark: row.remark ?? row.remarks ?? "",
        production_Seq_No: row.production_Seq_No,
        created_By: userId,
      };

      await qc.saveOrUpdate(payload);
      notify("Saved successfully", "success");
      setEditingRow(null);
      setBackupRow(null);
      setCurrentPage(1);
      fetchData(1, activeTab);
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to save";
      notify(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkDone = async (rowIndex) => {
    if (saving) return;
    setSaving(true);
    try {
      const row = isQcData[rowIndex];

      if (toInt(row.qc_Pcs) !== toInt(row.produced_Pcs)) {
        notify("QC Pcs must be equal to Produced Pcs to mark as done", "error");
        return;
      }

      if (
        toInt(row.produced_Pcs) !==
        toInt(row.approved_Pcs) + toInt(row.rejected_Pcs)
      ) {
        notify("Total (Appr + Rej) must match Produced Pcs", "error");
        return;
      }

      const payload = {
        die_Id: row.die_Id,
        LastPCSWeight:
          (toFloat(row.first_Weight) + toFloat(row.second_Weight)) / 2,
        created_By: userId,
        production_Seq_No: row.production_Seq_No,
      };

      await qc.MarkQCDone(payload);
      notify("Production marked as done", "success");

      setCurrentPage(1);
      fetchData(1, "PENDING");
    } catch (error) {
      notify("Failed to mark done", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRowChange = (rowIndex, field, raw) => {
    setIsQcData((prev) => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      row[field] = raw;

      const qcPcs = toInt(field === "qc_Pcs" ? raw : row.qc_Pcs);
      const rejPcs = toInt(field === "rejected_Pcs" ? raw : row.rejected_Pcs);
      row.approved_Pcs = String(Math.max(qcPcs - rejPcs, 0));

      next[rowIndex] = row;
      return next;
    });
  };

  const renderCell = (
    row,
    rowIndex,
    field,
    { type = "text", step, alignRight = true } = {},
  ) => {
    const editable = isRowEditable(rowIndex);

    if (editable) {
      return (
        <input
          type={type}
          step={step}
          value={row[field] ?? ""}
          onChange={(e) => handleRowChange(rowIndex, field, e.target.value)}
          className={`w-full bg-white border border-[#3F80F7] px-2 py-1 outline-none text-[12px]  ${alignRight ? "text-right" : "text-left"
            }`}
          autoFocus={field === "qc_Pcs"}
        />
      );
    }

    return (
      <div
        className={`px-3 py-1 text-gray-800 font-bold ${alignRight ? "text-right" : "text-left"}`}
      >
        {row[field] || row[field] === 0 ? row[field] : "-"}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white font-plus">
      <RegistryHeader title="Quality Control Completed">
        <div className="flex bg-gray-100 p-1 rounded-sm gap-1 shrink-0">
          <button
            onClick={() => {
              setActiveTab("PENDING");
              setCurrentPage(1);
            }}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "PENDING"
              ? "bg-[#3F80F7] text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            QC Pending
          </button>
          <button
            onClick={() => {
              setActiveTab("DONE");
              setCurrentPage(1);
            }}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "DONE"
              ? "bg-[#3F80F7] text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            QC Done
          </button>
        </div>

        <div className="relative w-full sm:w-80 group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#3F80F7]">
            <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
          </div>
          <input
            type="text"
            className="block w-full pl-12 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-[#3F80F7] focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
            >
              <MdClose size={18} />
            </button>
          )}
        </div>
      </RegistryHeader>

      <div className="px-8 pt-0 pb-4 flex flex-col flex-1 overflow-hidden">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="relative overflow-auto border border-gray-200 bg-white flex-1"
        >
          {loading && <Loader centered />}
          {activeTab === "PENDING" && (
            <div className="min-w-max bg-white">
              <Table
                columns={[
                  { key: "order_No", label: "SO NO", align: "center", className: "font-black text-slate-800 border-r border-gray-100 uppercase tracking-tighter" },
                  { key: "batch_Id", label: "BATCH", align: "left", className: "text-gray-500 border-r border-gray-100 font-bold uppercase tracking-tight" },
                  { key: "casting_No", label: "CASTING", align: "left", className: "font-black text-gray-800 border-r border-gray-100" },
                  { key: "dieNumber", label: "DIE", align: "left", className: "font-black text-gray-800 border-r border-gray-100 bg-gray-50/10" },
                  { key: "produced_Pcs", label: "PROD.", align: "right", className: "font-black text-gray-400 border-r border-gray-100" },
                  { key: "dispatch_Date", label: "DISPATCH DATE", align: "left", render: (val) => formatDate(val), className: "text-gray-400 border-r border-gray-100 font-bold tracking-tight" },
                  {
                    key: "qc_Pcs", label: "QC", align: "center", headerClassName: "bg-[#3F80F7]/5 text-[#3F80F7]", className: "bg-[#3F80F7]/5 border-r border-gray-100",
                    render: (val, row, idx) => renderCell(row, idx, "qc_Pcs", { type: "number" })
                  },
                  {
                    key: "rejected_Pcs", label: "REJ.", align: "center", headerClassName: "bg-red-50 text-red-600", className: "bg-red-50/10 border-r border-gray-100",
                    render: (val, row, idx) => renderCell(row, idx, "rejected_Pcs", { type: "number" })
                  },
                  { key: "approved_Pcs", label: "APPR.", align: "right", headerClassName: "bg-green-50 text-green-700", className: "font-black text-green-700 border-r border-gray-100 bg-green-50/5 text-[13px]" },
                  {
                    key: "first_Weight", label: "1ST WT", align: "center", className: "border-r border-gray-100",
                    render: (val, row, idx) => renderCell(row, idx, "first_Weight", { type: "number", step: "0.001" })
                  },
                  {
                    key: "second_Weight", label: "2ND WT", align: "center", className: "border-r border-gray-100",
                    render: (val, row, idx) => renderCell(row, idx, "second_Weight", { type: "number", step: "0.001" })
                  },
                  { key: "current_Weight", label: "CURR WT", align: "right", className: "font-bold text-gray-400 border-r border-gray-100", render: (val) => val || "-" },
                  { key: "cut_Length", label: "CUT LEN", align: "center", className: "font-bold text-gray-400 border-r border-gray-100", render: (val) => val || "-" },
                  {
                    key: "remark", label: "REASON/REMARK", align: "left", className: "border-r border-gray-100 min-w-[200px]",
                    render: (val, row, idx) => isRowEditable(idx) ? (
                     <div className="flex flex-col gap-1">
                            {renderCell(row, idx, "reject_Reason", {
                              alignRight: false,
                            })}
                            {renderCell(row, idx, "remark", {
                              alignRight: false,
                            })}
                          </div>
                    ) : (
                       <div className="text-[11px] text-slate-500">
                            {row.reject_Reason && (
                              <div>R: {row.reject_Reason}</div>
                            )}
                            {row.remark && <div>N: {row.remark}</div>}
                          </div>
                    )
                  },
                  {
                    key: "actions", label: "ACTIONS", align: "center",
                    render: (val, row, idx) => (
                      <div className="flex items-center justify-center gap-1">
                        {isRowEditable(idx) ? (
                          <>
                            <button
                              onClick={() => handleSubmit(idx)}
                              className="p-1.5 border border-gray-200 text-green-600 hover:bg-green-50 transition-all"
                              disabled={saving}
                            >
                              <IoIosSave size={16} />
                            </button>
                            <button
                              onClick={() => handleCancelEdit(idx)}
                              className="p-1.5 border border-gray-200 text-red-600 hover:bg-red-50 transition-all"
                              disabled={saving}
                            >
                              <MdClose size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(idx)}
                              className="p-1.5 border border-gray-200 text-blue-600 hover:bg-blue-50 transition-all"
                              title="Modify Parameters"
                            >
                              <MdModeEdit size={16} />
                            </button>
                            <button
                              onClick={() => handleMarkDone(idx)}
                              className="p-1.5 border border-gray-200 text-green-600 hover:bg-green-50 transition-all"
                              title="Complete Protocol"
                            >
                              <AiOutlineCheck size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    )
                  }
                ]}
                data={isQcData}
                showActions={false}
                getRowClassName={(row, idx) => isRowEditable(idx) ? "bg-[#3F80F7]/5" : ""}
                tableClassName="text-[12px] border-none"
              />
            </div>
          )}

          {activeTab === "DONE" && (
            <div className="min-w-max bg-white">
              <Table
                columns={[
                  { key: "order_No", label: "Order No" },
                  { key: "batch_Id", label: "Batch ID" },
                  { key: "casting_No", label: "Casting No" },
                  { key: "dieNumber", label: "Die Number" },
                  { key: "approved_Pcs", label: "QC Pcs" },
                  { key: "current_Weight", label: "Weight" },
                  { key: "cut_Length", label: "Length" },
                  {
                    key: "dispatch_Date",
                    label: "Dispatch Date",
                    render: (value) => formatDate(value),
                  },
                  { key: "packing_Instructions", label: "Packing Instruction" },
                  { key: "general_Notes", label: "Remark" },
                ]}
                data={isQcDoneData}
                showActions={false}
                tableClassName="text-[12px] border-none font-bold"
              />
            </div>
          )}
        </div>
      </div>

      <CalculationFooter
        items={[
          {
            label: "Total Production",
            value: isQcData.reduce((sum, row) => sum + toInt(row.produced_Pcs), 0).toLocaleString(),
            suffix: "PCS"
          },
          {
            label: "QC Pcs",
            value: isQcData.reduce((sum, row) => sum + toInt(row.qc_Pcs), 0).toLocaleString(),
            suffix: "PCS",
            color: "text-[#3F80F7]"
          },
          {
            label: "Rejected Pcs",
            value: isQcData.reduce((sum, row) => sum + toInt(row.rejected_Pcs), 0).toLocaleString(),
            suffix: "PCS",
            color: "text-red-600"
          },
          {
            label: "Approved Pcs",
            value: isQcData.reduce((sum, row) => sum + toInt(row.approved_Pcs), 0).toLocaleString(),
            suffix: "PCS",
            color: "text-green-600"
          }
        ]}
      />
    </div>
  );
};

export default QC;
